/**
 * Domain enums. Raw string values are IDENTICAL to the iOS/macOS app's
 * `rawValue`s (apps/apple/Bridewell/Models/Models.swift) so a future sync layer maps
 * rows 1:1 without a translation table.
 */

export const GUEST_RSVP = ["pending", "accepted", "declined", "maybe"] as const;
export type GuestRSVP = (typeof GUEST_RSVP)[number];

export const GUEST_SIDE = ["partnerOne", "partnerTwo", "both"] as const;
export type GuestSide = (typeof GUEST_SIDE)[number];

export const GUEST_CATEGORY = ["family", "friend", "work", "plusOne", "child", "vendor", "other"] as const;
export type GuestCategory = (typeof GUEST_CATEGORY)[number];

export const BUDGET_CATEGORY = [
  "venue", "catering", "attire", "photography", "music", "flowers", "decor",
  "stationery", "rings", "transport", "beauty", "gifts", "honeymoon", "other",
] as const;
export type BudgetCategory = (typeof BUDGET_CATEGORY)[number];

export const CHECKLIST_TIMEFRAME = [
  "twelvePlus", "nineToTwelve", "sixToNine", "threeToSix", "oneToThree",
  "oneMonth", "weekOf", "dayOf", "after", "custom",
] as const;
export type ChecklistTimeframe = (typeof CHECKLIST_TIMEFRAME)[number];

export const SHOPPING_CATEGORY = ["attire", "decor", "favors", "beauty", "stationery", "accessories", "other"] as const;
export type ShoppingCategory = (typeof SHOPPING_CATEGORY)[number];

export const INSPIRATION_CATEGORY = [
  "dress", "decor", "flowers", "cake", "venue", "hair", "makeup", "rings", "suit", "shoes", "other",
] as const;
export type InspirationCategory = (typeof INSPIRATION_CATEGORY)[number];

export const VENDOR_TYPE = [
  "venue", "caterer", "photographer", "videographer", "florist", "dj", "band",
  "plannerCoord", "baker", "dressShop", "suitShop", "makeup", "hair", "transport",
  "officiant", "stationery", "rentals", "other",
] as const;
export type VendorType = (typeof VENDOR_TYPE)[number];

export const VENDOR_STATUS = ["researching", "contacted", "quoted", "booked", "paid", "completed"] as const;
export type VendorStatus = (typeof VENDOR_STATUS)[number];

export const TABLE_SHAPE = ["round", "rectangle"] as const;
export type TableShape = (typeof TABLE_SHAPE)[number];

/**
 * Timeline event visibility outside the planning team. Web-only (no iOS field; iOS ignores the column and a row
 * without it means "team"): private = never shared, team = shared run sheets, guests = also on the guest RSVP page.
 */
export const TIMELINE_VISIBILITY = ["private", "team", "guests"] as const;
export type TimelineVisibility = (typeof TIMELINE_VISIBILITY)[number];

export const ACTIVITY_ACTION = ["create", "edit", "delete"] as const;
export type ActivityAction = (typeof ACTIVITY_ACTION)[number];

/** Per-module permission surface. Mirrors `AccessModule` on iOS (12 modules). */
export const ACCESS_MODULE = [
  "overview", "checklist", "guests", "budget", "vendors", "timeline",
  "seating", "notes", "inspiration", "shopping", "registry", "activity",
] as const;
export type AccessModule = (typeof ACCESS_MODULE)[number];

export const ACCESS_LEVEL = ["hidden", "view", "edit"] as const;
export type AccessLevel = (typeof ACCESS_LEVEL)[number];

export const COLLABORATOR_ROLE = ["owner", "coOwner", "planner", "editor", "viewer", "reader", "guestHelper", "custom"] as const;
export type CollaboratorRole = (typeof COLLABORATOR_ROLE)[number];

/**
 * Account tiers, lowest to highest. `user.plan` holds one of these; it is derived
 * from `entitlements` by BillingService.syncPlan and never set by a client.
 * iOS knows only free/premium today: when sync arrives it must treat any value
 * it does not recognise as "premium or better", never as free.
 */
export const PLAN = ["free", "premium", "pro", "enterprise"] as const;
export type Plan = (typeof PLAN)[number];

export const PLAN_RANK: Record<Plan, number> = { free: 0, premium: 1, pro: 2, enterprise: 3 };

export function isPlan(v: unknown): v is Plan {
  return typeof v === "string" && (PLAN as readonly string[]).includes(v);
}

/** Unknown or corrupt values fail closed to free. */
export function resolvePlan(v: unknown): Plan {
  return isPlan(v) ? v : "free";
}

export function atLeastPlan(plan: Plan, min: Plan): boolean {
  return PLAN_RANK[plan] >= PLAN_RANK[min];
}

export function higherPlan(a: Plan, b: Plan): Plan {
  return PLAN_RANK[a] >= PLAN_RANK[b] ? a : b;
}

/** ISO-4217 codes the picker offers. Same list as `Money.supportedCurrencies` on iOS. */
export const SUPPORTED_CURRENCIES = [
  "AED", "ARS", "AUD", "BHD", "BRL", "CAD", "CHF", "CLP", "CNY",
  "COP", "DKK", "EGP", "EUR", "GBP", "IDR", "INR", "JPY", "KRW",
  "KWD", "MAD", "MXN", "NOK", "NZD", "PEN", "QAR", "RUB", "SAR",
  "SEK", "SGD", "TRY", "USD", "ZAR",
] as const;
export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];

export function resolveCurrency(code: string | null | undefined): CurrencyCode {
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(code ?? "") ? (code as CurrencyCode) : "USD";
}
